/*
 * Copyright (C) 2023 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  AfterContentInit,
  ChangeDetectorRef,
  Directive,
  EmbeddedViewRef,
  Input,
  OnDestroy,
  Optional,
  Renderer2,
  TemplateRef,
  ViewContainerRef
} from '@angular/core';
import { combineLatest, Subscription } from 'rxjs';
import { isNgfAbstractControl, NgfAbstractControl } from '../../+model/ngf-abstract-control';
import { NgfControlComponent } from '../ngf-control.component';

@Directive({
    selector: '[ngfControlHasError]',
    standalone: false
})
export class NgfControlHasErrorDirective implements AfterContentInit, OnDestroy {

  @Input('ngfControlHasError')
  errorKey: string;
  @Input('ngfControlHasErrorControl')
  control: NgfAbstractControl | null = null;

  private sub = Subscription.EMPTY;
  private embeddedViewRef: EmbeddedViewRef<any> | null = null;
  private srOnlyContainer: HTMLDivElement | null = null;

  constructor(private templateRef: TemplateRef<any>,
              private vcr: ViewContainerRef,
              private cdr: ChangeDetectorRef,
              private renderer: Renderer2,
              @Optional() private ngfControlComponent: NgfControlComponent | null = null) {
  }

  // using ngAfterContentInit hook
  // to wait for ngf-control setup
  ngAfterContentInit(): void {
    const control = this.getNgfControl();

    this.sub = combineLatest([control.touched$, control.errors$])
      .subscribe(([touched, errors]) => {
        if (touched && errors?.hasOwnProperty(this.errorKey)) {
          this.createView(errors[this.errorKey]);
        } else {
          this.destroyView();
        }

        // both control touched state and errors, might be changed outside the current change detection
        // ex. user click ngfFormBtnCancel outside the form component
        // in such cases components (like ngf-control) will not be notified about errors being hidden or shown
        // therefor we have to call "markForCheck"
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  private getNgfControl(): NgfAbstractControl {
    // if control was specified then use it
    if (this.control !== null) {
      return this.control;
    }

    // if control is not specified we will try to
    // infer it from NgfControlComponent
    if (this.ngfControlComponent === null) {
      throw new NgfControlComponentNotFoundError(this.errorKey);
    }

    // if is possible to create <ngf-control> without any [formControl] directive inside,
    // in such case we are not able to infer NgfFormControl
    if (this.ngfControlComponent.formControlDirectives.length === 0) {
      throw new FormControlDirectiveNotFoundFoundError(this.errorKey);
    }

    // it is possible to create <ngf-control> with multiple [formControl] directive inside,
    // but we don't want to iterate over each one and check of given error key is present at any of it
    // doing so would:
    //  - increase complexity of this component
    //  - make it harder (when reading the template) to guess which error comes from which FormControl
    // therefor we will do inferring of NgfFormControl only if one [formControl] is used
    if (this.ngfControlComponent.formControlDirectives.length > 1) {
      throw new MoreThanOneFormControlDirectiveFoundError(this.errorKey);
    }

    const control = this.ngfControlComponent.formControlDirectives.get(0).control;

    if (!isNgfAbstractControl(control)) {
      throw new FormControlDirectiveIsNotUsingNgfFormControlError();
    }

    return control;
  }

  private createView(error: any): void {
    if (this.embeddedViewRef !== null) {
      return;
    }

    this.embeddedViewRef = this.vcr.createEmbeddedView(this.templateRef, { $implicit: error });
    this.embeddedViewRef.markForCheck();

    this.setupScreenReaderElement();
  }

  private setupScreenReaderElement() {
    // Create sr-only wrapper div for screen reader announcement
    this.srOnlyContainer = this.renderer.createElement('div');

    this.setScreenReaderStyling();
    this.setScreenReaderText();
    this.attachScreenReaderContainerToView();
  }

  private setScreenReaderStyling() {
    this.renderer.setStyle(this.srOnlyContainer, "position", "absolute");
    this.renderer.setStyle(this.srOnlyContainer, "width", "1px");
    this.renderer.setStyle(this.srOnlyContainer, "height", "1px");
    this.renderer.setStyle(this.srOnlyContainer, "padding", "0");
    this.renderer.setStyle(this.srOnlyContainer, "margin", "-1px");
    this.renderer.setStyle(this.srOnlyContainer, "overflow", "hidden");
    this.renderer.setStyle(this.srOnlyContainer, "clip", "rect(0, 0, 0, 0)");
    this.renderer.setStyle(this.srOnlyContainer, "white-space", "nowrap");
    this.renderer.setStyle(this.srOnlyContainer, "border", "0");

    this.renderer.setAttribute(this.srOnlyContainer, 'aria-live', 'assertive');
    this.renderer.setAttribute(this.srOnlyContainer, 'aria-atomic', 'true');
  }

  private setScreenReaderText() {
    // Extract text content from the embedded view for screen reader announcement
    // We use a small delay to ensure the view is fully rendered
    setTimeout(() => {
      if (this.embeddedViewRef && this.srOnlyContainer) {
        const textContent = this.embeddedViewRef.rootNodes
          .map((node: any) => node.textContent || '')
          .join(' ')
          .trim();

        if (textContent) {
          const textNode = this.renderer.createText(textContent);
          this.renderer.appendChild(this.srOnlyContainer, textNode);
        }
      }
    }, 0);
  }

  private attachScreenReaderContainerToView() {
    // Append the sr-only wrapper to the view container's element
    const parentElement = this.vcr.element.nativeElement.parentElement;
    if (parentElement) {
      this.renderer.appendChild(parentElement, this.srOnlyContainer);
    }
  }

  private destroyView(): void {
    if (this.embeddedViewRef === null) {
      return;
    }

    this.embeddedViewRef.destroy();
    this.embeddedViewRef = null;

    // Remove the sr-only container
    if (this.srOnlyContainer !== null) {
      this.renderer.removeChild(this.srOnlyContainer.parentElement, this.srOnlyContainer);
      this.srOnlyContainer = null;
    }
  }
}

class NgfControlComponentNotFoundError extends Error {
  constructor(errorKey: string) {
    super(`
[NgfControlErrorDirective] <ngf-control> not found, either:
 - specify control using "control" input
    <ngf-control-error *ngfControlHasError="'${errorKey}'; control: yourNgfControl">...</ngf-control-error>
 - or move *ngfControlHasError into <ngf-control> content, to automatically infer it:
    <ngf-control>
      <input [formControl]="yourNgfControl">
      <ngf-form-error *ngfControlHasError="'${errorKey}'">...</ngf-form-error>
    </ngf-control>
`);
  }
}

class FormControlDirectiveNotFoundFoundError extends Error {
  constructor(errorKey: string) {
    super(`
[NgfControlErrorDirective] <ngf-control> does not contains any [formControl] directive.
Please specify which control should be used with:
  <ng-container *ngfControlHasError="'${errorKey}'; control: yourNgfControl">...</ng-container>
`);
  }
}

class MoreThanOneFormControlDirectiveFoundError extends Error {
  constructor(errorKey: string) {
    super(`
[NgfControlErrorDirective] <ngf-control> contains more than one [formControl] directive.
Please specify which control should be used with:
  <ngf-form-errors-counter *ngfControlHasError="'${errorKey}'; control: yourNgfControl">...</ngf-form-errors-counter>
`);
  }
}

class FormControlDirectiveIsNotUsingNgfFormControlError extends Error {
  constructor() {
    super(`
[NgfControlErrorDirective] Directive [formControl] is used with native FormControl class.
Please switch to NgfFormControl instead.
`);
  }
}
